import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, map, take } from 'rxjs';
import { AppGlobalService } from '../services/app.global.service';
import { userData } from '../STORE/selectors/auth.selector';

interface RouteContext {
  orgId: string;
  appId: string;
  topLevel: string;
  secondLevel: string;
}

@Injectable({
  providedIn: 'root'
})
export class VibeRouteGuard implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly appGlobalService = inject(AppGlobalService);

  canActivateChild(
    _childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.store.select(userData).pipe(
      take(1),
      map((user: any) => {
        if (!user?.email) {
          return this.router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
        }

        this.appGlobalService.write('isBetaAccessEnabled', !!user.betaAccess);
        this.appGlobalService.write('raiModeEnabledForUser', !!user.raiModeEnabled);

        if (!this.shouldBlockForNonVibeUser(state.url)) {
          return true;
        }

        return this.createFallbackUrlTree(state.url, user);
      })
    );
  }

  private shouldBlockForNonVibeUser(url: string): boolean {
    const { topLevel, secondLevel } = this.getRouteContext(url);

    // Org-level dashboards are not app-scoped vibe/beta features, so never block them.
    if (topLevel === 'dashboards' && ['clouds-centre', 'billing-home'].includes(secondLevel)) {
      return false;
    }

    if (['code', 'operate', 'blueprint', 'dashboards', 'app-dashboards'].includes(topLevel)) {
      return !this.appGlobalService.isBetaAccessPrivilegeConsumableForApp();
    }

    if (topLevel === 'settings' && secondLevel === 'environment-availability') {
      return !this.appGlobalService.isBetaAccessPrivilegeConsumableForApp();
    }

    return false;
  }

  private createFallbackUrlTree(url: string, user: any): UrlTree {
    const context = this.getRouteContext(url);
    const orgId = context.orgId || this.appGlobalService.get('orgId') || user?.orgIds?.[0] || '';
    const appId = context.appId || this.appGlobalService.get('appId') || '';

    if (appId && appId !== 'appId') {
      return this.router.createUrlTree(['/home', orgId, appId, 'gathering', 'documents']);
    }

    return this.router.createUrlTree(['/home', orgId, 'appId', 'settings', 'appmanagement']);
  }

  private getRouteContext(url: string): RouteContext {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter((segment) => segment.length > 0);
    const homeIndex = segments.indexOf('home');

    if (homeIndex < 0) {
      return {
        orgId: this.appGlobalService.get('orgId') || '',
        appId: this.appGlobalService.get('appId') || '',
        topLevel: '',
        secondLevel: ''
      };
    }

    return {
      orgId: segments[homeIndex + 1] || '',
      appId: segments[homeIndex + 2] || '',
      topLevel: segments[homeIndex + 3] || '',
      secondLevel: segments[homeIndex + 4] || ''
    };
  }
}